import * as maplibregl from "maplibre-gl";
import {
  HOME_BOUNDS,
  WAYPOINT_COLORS,
  WAYPOINT_FALLBACK,
  bindBasemapSwitch,
  boundsOf,
  branchLabel,
  buildStyle,
  el,
  escapeHtml,
  fmtDrive,
  fmtMiles,
  json,
  shortGoalName,
} from "./shared";

type Waypoint = {
  type: string;
  name: string;
  notes: string;
  date: string;
  time: string;
  day: number | null;
  approximate: boolean;
  offRiver: boolean;
  riverMile: number;
  tripId: string;
  tripName: string;
};

type Day = {
  n: number;
  date?: string;
  label?: string;
  from?: string;
  to?: string;
  camp?: string;
};

type Trip = {
  id: string;
  name: string;
  date: string;
  endDate: string | null;
  branch: string;
  status: "completed" | "active" | "planned";
  tier: "weekend" | "ambitious" | "chain" | null;
  color: string;
  days: Day[];
  paddlers: string[];
  paddlerCount: number;
  notes: string;
  photos: { src: string; caption?: string }[];
  miles: number;
  upstream: boolean;
  driveMinutes: number | null;
  unlocks: { goal: string; name: string; miles: number }[];
  putIn: { name: string; riverMile: number };
  takeOut: { name: string; riverMile: number };
  geometry: GeoJSON.LineString;
};

type MileSpan = {
  branch: string;
  branchName: string;
  fromMile: number;
  toMile: number;
  paddledMiles: number;
  totalMiles: number;
  miles: { m: number; f: number; t: string[]; c: [number, number] }[];
};

type Goal = {
  id: string;
  name: string;
  blurb: string;
  link: string | null;
  published: number | null;
  totalMiles: number;
  paddledMiles: number;
  remainingMiles: number;
  percent: number;
  segments?: {
    name: string;
    published: number | null;
    totalMiles: number;
    paddledMiles: number;
    percent: number;
  }[];
};

type Stats = {
  goalMiles: number;
  paddledMiles: number;
  remainingMiles: number;
  percent: number;
  tripCount: number;
  plannedCount: number;
  branches: { id: string; name: string; paMiles: number; paddledPaMiles: number }[];
};

// Stops that anchor the trip, as opposed to things noted in passing.
const MAJOR = new Set(["campsite", "camp", "put-in", "take-out", "launch", "town", "hazard"]);

// Per-day accent, following the trip pages.
const DAY_COLORS = ["#e2701e", "#2e93a8", "#a8437e", "#7fa650", "#c8994a"];

function fmtDateRange(date: string, endDate: string | null): string {
  // Dates are plain YYYY-MM-DD; parsing and formatting in UTC keeps them from
  // sliding a day backwards in western time zones.
  const opts: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  };
  const start = new Date(`${date}T00:00:00Z`);
  if (!endDate || endDate === date) return start.toLocaleDateString("en-US", opts);

  const end = new Date(`${endDate}T00:00:00Z`);
  const sameMonth =
    start.getUTCFullYear() === end.getUTCFullYear() &&
    start.getUTCMonth() === end.getUTCMonth();
  if (sameMonth) {
    const month = start.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
    return `${month} ${start.getUTCDate()}–${end.getUTCDate()}, ${start.getUTCFullYear()}`;
  }
  return `${start.toLocaleDateString("en-US", opts)} – ${end.toLocaleDateString("en-US", opts)}`;
}


async function main() {
  const [stats, allTrips, paddled, planned, remaining, waypoints, ledger, goals] = await Promise.all([
    json<Stats>("data/stats.json"),
    json<Trip[]>("data/trips.json"),
    json<GeoJSON.FeatureCollection>("data/paddled.geojson"),
    json<GeoJSON.FeatureCollection>("data/planned.geojson"),
    json<GeoJSON.FeatureCollection>("data/remaining.geojson"),
    json<GeoJSON.FeatureCollection>("data/waypoints.geojson"),
    json<MileSpan[]>("data/miles.json"),
    json<Goal[]>("data/goals.json"),
  ]);

  // Long trips and chain links live on their own pages; this map keeps to the
  // weekend options and what we have done.
  const isLong = (f: GeoJSON.Feature) =>
    f.properties?.tier === "ambitious" || f.properties?.tier === "chain";
  planned.features = planned.features.filter((f) => !isLong(f));
  waypoints.features = waypoints.features.filter((f) => !isLong(f));
  const trips = allTrips.filter((t) => t.tier !== "ambitious" && t.tier !== "chain");

  renderStats(stats);
  renderGoals(goals);

  const map = new maplibregl.Map({
    container: "map",
    style: buildStyle(),
    bounds: HOME_BOUNDS,
    fitBoundsOptions: { padding: 40 },
    attributionControl: { compact: true },
  });
  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
  map.addControl(new maplibregl.ScaleControl({ unit: "imperial" }), "bottom-right");

  const popup = new maplibregl.Popup({ closeButton: false, offset: 12, maxWidth: "250px" });

  // Bind to `style.load`, not `load`. The `load` event also waits on the first
  // tiles, so a basemap that is slow or unreachable would keep the river layers
  // from ever being added and leave the map blank — the river must not depend
  // on the imagery underneath it.
  const addRiverLayers = () => {
    map.addSource("remaining", { type: "geojson", data: remaining });
    map.addSource("paddled", { type: "geojson", data: paddled });
    map.addSource("planned", { type: "geojson", data: planned });
    map.addSource("waypoints", { type: "geojson", data: waypoints });
    map.addSource("selected", {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });

    // Everything still to do reads as a faint dashed thread, so the whole goal
    // is visible at once and the paddled miles are what stand out.
    map.addLayer({
      id: "remaining-line",
      type: "line",
      source: "remaining",
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": "#31586a",
        "line-opacity": 0.85,
        "line-dasharray": [2, 2.5],
        "line-width": ["interpolate", ["linear"], ["zoom"], 6, 1.4, 10, 2.6, 14, 4],
      },
    });

    // A dark casing under each line keeps it legible over satellite imagery.
    for (const [id, source, color] of [
      ["paddled-casing", "paddled", "#0e1f26"],
      ["selected-casing", "selected", "#0e1f26"],
    ] as const) {
      map.addLayer({
        id,
        type: "line",
        source,
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": color,
          "line-opacity": 0.45,
          "line-width": ["interpolate", ["linear"], ["zoom"], 6, 6, 10, 10, 14, 15],
        },
      });
    }

    map.addLayer({
      id: "paddled-line",
      type: "line",
      source: "paddled",
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": ["get", "color"],
        "line-width": ["interpolate", ["linear"], ["zoom"], 6, 2.4, 10, 4.5, 14, 7],
      },
    });

    // Proposals read as dashed and cool, so they never look like miles earned.
    map.addLayer({
      id: "planned-line",
      type: "line",
      source: "planned",
      layout: { "line-cap": "butt", "line-join": "round" },
      paint: {
        "line-color": ["get", "color"],
        "line-dasharray": [3, 2],
        "line-width": ["interpolate", ["linear"], ["zoom"], 6, 2, 10, 3.5, 14, 5],
      },
    });

    map.addLayer({
      id: "selected-line",
      type: "line",
      source: "selected",
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": "#e4ecea",
        "line-width": ["interpolate", ["linear"], ["zoom"], 6, 3, 10, 5.5, 14, 8],
        // Dashed when the selection is a proposal, so highlighting it never
        // makes unpaddled water look paddled.
        "line-dasharray": ["case", ["get", "planned"], ["literal", [3, 2]], ["literal", [1, 0]]],
      },
    });

    const colorByType = [
      "match",
      ["get", "type"],
      ...Object.entries(WAYPOINT_COLORS).flat(),
      WAYPOINT_FALLBACK,
    ] as unknown as maplibregl.ExpressionSpecification;

    const isMajor = [
      "in",
      ["get", "type"],
      ["literal", [...MAJOR]],
    ] as unknown as maplibregl.ExpressionSpecification;

    map.addLayer({
      id: "waypoint-dots",
      type: "circle",
      source: "waypoints",
      paint: {
        "circle-color": colorByType,
        "circle-radius": [
          "interpolate",
          ["linear"],
          ["zoom"],
          7,
          ["case", isMajor, 5, 3],
          12,
          ["case", isMajor, 8, 5],
        ],
        "circle-stroke-color": "#0e1f26",
        "circle-stroke-width": 1.5,
        // Interpolated island positions are drawn softer than surveyed points.
        "circle-opacity": ["case", ["get", "approximate"], 0.75, 1],
      },
    });

    map.on("mouseenter", "waypoint-dots", (e) => {
      map.getCanvas().style.cursor = "pointer";
      const f = e.features?.[0];
      if (!f) return;
      const p = f.properties as unknown as Waypoint;
      const mile = p.offRiver
        ? "Off river"
        : `Mile ${p.riverMile}${p.approximate ? " (approx)" : ""}`;
      popup
        .setLngLat((f.geometry as GeoJSON.Point).coordinates as [number, number])
        .setHTML(
          `<span class="popup-mile">${escapeHtml(mile)}${p.time ? ` · ${escapeHtml(p.time)}` : ""}</span>` +
            `<span class="popup-title">${escapeHtml(p.name)}</span>` +
            (p.notes ? `<div class="popup-body">${escapeHtml(p.notes)}</div>` : ""),
        )
        .addTo(map);
    });
    map.on("mouseleave", "waypoint-dots", () => {
      map.getCanvas().style.cursor = "";
      popup.remove();
    });
    map.on("click", "waypoint-dots", (e) => {
      const id = (e.features?.[0]?.properties as { tripId?: string })?.tripId;
      const trip = trips.find((t) => t.id === id);
      if (trip) select(trip);
    });

    map.on("click", "planned-line", (e) => {
      const id = (e.features?.[0]?.properties as { tripId?: string })?.tripId;
      const trip = trips.find((t) => t.id === id);
      if (trip) select(trip);
    });

    map.on("click", "paddled-line", (e) => {
      // Pick the trip whose line passes closest to the click.
      let best: Trip | null = null;
      let bestDist = Infinity;
      for (const trip of trips) {
        for (const c of trip.geometry.coordinates) {
          const d = (c[0] - e.lngLat.lng) ** 2 + (c[1] - e.lngLat.lat) ** 2;
          if (d < bestDist) {
            bestDist = d;
            best = trip;
          }
        }
      }
      if (best) select(best);
    });
  };

  if (map.isStyleLoaded()) addRiverLayers();
  else map.once("style.load", addRiverLayers);

  // --- basemap switching ---

  bindBasemapSwitch(map);

  // --- selection ---

  const wpFor = (tripId: string) =>
    waypoints.features
      .map((f) => f.properties as unknown as Waypoint)
      .filter((p) => p.tripId === tripId);

  let current: Trip | null = null;

  function focusWaypoint(name: string) {
    const f = waypoints.features.find(
      (x) => (x.properties as unknown as Waypoint).name === name,
    );
    if (!f) return;
    const p = f.properties as unknown as Waypoint;
    const at = (f.geometry as GeoJSON.Point).coordinates as [number, number];
    map.flyTo({ center: at, zoom: Math.max(map.getZoom(), 13), duration: 700 });
    const mile = p.offRiver ? "Off river" : `Mile ${p.riverMile}${p.approximate ? " (approx)" : ""}`;
    popup
      .setLngLat(at)
      .setHTML(
        `<span class="popup-mile">${escapeHtml(mile)}${p.time ? ` · ${escapeHtml(p.time)}` : ""}</span>` +
          `<span class="popup-title">${escapeHtml(p.name)}</span>` +
          (p.notes ? `<div class="popup-body">${escapeHtml(p.notes)}</div>` : ""),
      )
      .addTo(map);
  }

  function select(trip: Trip) {
    current = trip;
    (map.getSource("selected") as maplibregl.GeoJSONSource | undefined)?.setData({
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: { planned: trip.status === "planned" },
          geometry: trip.geometry,
        },
      ],
    });
    map.fitBounds(boundsOf(trip.geometry.coordinates), {
      padding: { top: 60, bottom: 60, left: 60, right: 420 },
      maxZoom: 12,
      duration: 900,
    });
    renderDetail(trip, wpFor(trip.id), focusWaypoint);
    for (const btn of document.querySelectorAll<HTMLButtonElement>(".trip, .next")) {
      btn.setAttribute("aria-current", String(btn.dataset.tripId === trip.id));
    }
  }

  function clearSelection() {
    current = null;
    popup.remove();
    (map.getSource("selected") as maplibregl.GeoJSONSource | undefined)?.setData({
      type: "FeatureCollection",
      features: [],
    });
    el("detail").classList.add("hidden");
    for (const btn of document.querySelectorAll<HTMLButtonElement>(".trip, .next")) {
      btn.setAttribute("aria-current", "false");
    }
  }

  renderLedger(ledger, trips, (at) =>
    map.flyTo({ center: at, zoom: Math.max(map.getZoom(), 11), duration: 800 }),
  );

  renderTripList(
    trips.filter((t) => t.status !== "planned"),
    select,
  );
  renderNextTrips(
    trips.filter((t) => t.status === "planned"),
    select,
  );

  el("reset-view").addEventListener("click", () => {
    clearSelection();
    map.fitBounds(HOME_BOUNDS, { padding: 40, duration: 900 });
  });
  el("detail-close").addEventListener("click", clearSelection);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && current) clearSelection();
  });
}

function renderStats(stats: Stats) {
  el("stat-paddled").textContent = fmtMiles(stats.paddledMiles);
  el("stat-goal").textContent = fmtMiles(stats.goalMiles);
  el("stat-percent").textContent = `${stats.percent}%`;
  el("stat-remaining").textContent = `${fmtMiles(stats.remainingMiles)} mi`;

  // Arc length as a fraction of the circle's circumference.
  const circumference = 2 * Math.PI * 52;
  const fill = el<HTMLElement & SVGCircleElement>("ring-fill");
  requestAnimationFrame(() => {
    const done = (stats.percent / 100) * circumference;
    fill.style.strokeDasharray = `${done} ${circumference - done}`;
  });

  el("branch-stats").innerHTML = stats.branches
    .map(
      (b) => `<li>
        <span class="branch-name">${escapeHtml(shortBranchName(b.name))}</span>
        <span class="branch-value">${fmtMiles(b.paddledPaMiles)} / ${fmtMiles(b.paMiles)} mi</span>
      </li>`,
    )
    .join("");
}

const shortBranchName = (name: string) =>
  name.replace(/ Susquehanna River$/, "").replace(/^Susquehanna River$/, "Main stem");

/**
 * The four goals, each a ratio against a fixed limit — so each is a meter, not
 * a chart. The West Branch club also breaks into segments, which get their own
 * thinner meters underneath.
 */
function renderGoals(goals: Goal[]) {
  const meter = (percent: number) =>
    `<div class="meter" role="img" aria-label="${percent}% complete"><i style="width:0%" data-pct="${percent}"></i></div>`;

  el("goal-list").innerHTML = goals
    .map((g) => {
      const title = g.link
        ? `<a href="${escapeHtml(g.link)}" target="_blank" rel="noopener">${escapeHtml(g.name)}</a>`
        : escapeHtml(g.name);
      const published =
        g.published && Math.abs(g.published - g.totalMiles) >= 0.5
          ? ` · the club calls it ${fmtMiles(g.published)}`
          : "";
      const segments = g.segments?.length
        ? `<ul class="goal-segments">${g.segments
            .map(
              (seg) => `<li>
                <span>${escapeHtml(seg.name)}</span>
                ${meter(seg.percent)}
                <span>${fmtMiles(seg.totalMiles)} mi</span>
              </li>`,
            )
            .join("")}</ul>`
        : "";

      return `<div class="goal">
        <div class="goal-head">
          <span class="goal-name">${title}</span>
          <span class="goal-pct">${g.percent}%</span>
        </div>
        <p class="goal-blurb">${escapeHtml(g.blurb)}</p>
        ${meter(g.percent)}
        <p class="goal-figures">${fmtMiles(g.paddledMiles)} of ${fmtMiles(g.totalMiles)} mi · ${fmtMiles(g.remainingMiles)} to go${published}</p>
        ${segments}
      </div>`;
    })
    .join("");

  requestAnimationFrame(() => {
    for (const fill of document.querySelectorAll<HTMLElement>(".meter > i")) {
      // A goal with a sliver of progress still shows a sliver.
      const pct = Number(fill.dataset.pct);
      fill.style.width = `${pct > 0 ? Math.max(pct, 0.8) : 0}%`;
    }
  });
}

/**
 * The mile ledger: every whole river mile in Pennsylvania as one dash, in
 * downstream order. Filled dashes are miles we have paddled; a mile only
 * partly covered is drawn partly filled, so the fraction is carried by
 * geometry and the colour stays a clean two-state.
 */
function renderLedger(
  spans: MileSpan[],
  trips: Trip[],
  onPick: (at: [number, number]) => void,
) {
  const host = el("ledger-spans");
  const tripName = new Map(trips.map((t) => [t.id, t.name]));
  const tripColor = new Map(trips.map((t) => [t.id, t.color]));
  // Trips are ordered oldest first, so the last one covering a mile is the
  // most recent — that is the colour the dash takes.
  const colorFor = (ids: string[]) =>
    ids.length ? (tripColor.get(ids[ids.length - 1]) ?? "#e2701e") : "";

  const tip = document.createElement("div");
  tip.id = "mile-tip";
  tip.className = "hidden";
  document.body.appendChild(tip);

  host.innerHTML = spans
    .map((span) => {
      const label =
        `${escapeHtml(shortBranchName(span.branchName))} · RM ${span.fromMile}–${span.toMile}`;
      const dashes = span.miles
        .map((m) => {
          const cls = m.f >= 0.995 ? "dash done" : m.f > 0 ? "dash part" : "dash";
          const color = colorFor(m.t);
          const tint = color ? ` style="background:${color}"` : "";
          const inner =
            m.f > 0 && m.f < 0.995
              ? `<i style="height:${(m.f * 100).toFixed(0)}%;background:${color}"></i>`
              : "";
          return `<span class="${cls}"${m.f >= 0.995 ? tint : ""} data-mile="${m.m}" data-f="${m.f}" data-lon="${m.c[0]}" data-lat="${m.c[1]}" data-trips="${escapeHtml(m.t.join("|"))}">${inner}</span>`;
        })
        .join("");
      return `<div class="ledger-span">
        <div class="ledger-label"><span>${label}</span><span><b>${fmtMiles(span.paddledMiles)}</b> / ${fmtMiles(span.totalMiles)} mi</span></div>
        <div class="dashes" role="img" aria-label="${escapeHtml(
          `${shortBranchName(span.branchName)}, river mile ${span.fromMile} to ${span.toMile}: ${fmtMiles(span.paddledMiles)} of ${fmtMiles(span.totalMiles)} miles paddled`,
        )}">${dashes}</div>
      </div>`;
    })
    .join("");

  const dashAt = (target: EventTarget | null): HTMLElement | null => {
    const node = (target as HTMLElement)?.closest?.(".dash") as HTMLElement | null;
    return node?.dataset.mile ? node : null;
  };

  host.addEventListener("mousemove", (e) => {
    const dash = dashAt(e.target);
    if (!dash) {
      tip.classList.add("hidden");
      return;
    }
    const f = Number(dash.dataset.f);
    const ids = (dash.dataset.trips ?? "").split("|").filter(Boolean);
    const state =
      f >= 0.995 ? "paddled" : f > 0 ? `${Math.round(f * 100)}% paddled` : "not yet";
    const who = ids.map((id) => tripName.get(id) ?? id).join(", ");
    tip.innerHTML =
      `Mile ${dash.dataset.mile} · ${escapeHtml(state)}` +
      (who ? `<br><span class="tip-trip">${escapeHtml(who)}</span>` : "");
    tip.classList.remove("hidden");
    // Keep the tooltip inside the viewport when near the right edge.
    const width = tip.offsetWidth;
    tip.style.left = `${Math.min(e.clientX + 12, window.innerWidth - width - 8)}px`;
    tip.style.top = `${e.clientY + 14}px`;
  });

  host.addEventListener("mouseleave", () => tip.classList.add("hidden"));

  host.addEventListener("click", (e) => {
    const dash = dashAt(e.target);
    if (!dash) return;
    onPick([Number(dash.dataset.lon), Number(dash.dataset.lat)]);
  });
}

function renderTripList(trips: Trip[], onSelect: (t: Trip) => void) {
  const list = el("trip-list");
  if (!trips.length) {
    list.innerHTML = `<p class="trip-meta">No trips logged yet.</p>`;
    return;
  }

  const byYear = new Map<string, Trip[]>();
  for (const trip of trips) {
    const year = trip.date.slice(0, 4);
    if (!byYear.has(year)) byYear.set(year, []);
    byYear.get(year)!.push(trip);
  }

  list.innerHTML = [...byYear.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(
      ([year, yearTrips]) => `<div class="year-group">
        <h3 class="year-label">${year}</h3>
        ${yearTrips
          .map(
            (t) => `<button class="trip" type="button" data-trip-id="${escapeHtml(t.id)}" aria-current="false">
              <i class="trip-swatch" style="background:${t.color}"></i><span class="trip-name">${escapeHtml(t.name)}${
                t.status === "planned" ? `<span class="trip-tag planned">Planned</span>` : ""
              }${
                t.status === "active" ? `<span class="trip-tag active">On the water</span>` : ""
              }</span>
              <span class="trip-meta">${fmtMiles(t.miles)} mi · ${escapeHtml(branchLabel(t.branch))}</span>
            </button>`,
          )
          .join("")}
      </div>`,
    )
    .join("");

  for (const btn of list.querySelectorAll<HTMLButtonElement>(".trip")) {
    btn.addEventListener("click", () => {
      const trip = trips.find((t) => t.id === btn.dataset.tripId);
      if (trip) onSelect(trip);
    });
  }
}

/** The five proposals, with what each would add to the goals. */
function renderNextTrips(trips: Trip[], onSelect: (t: Trip) => void) {
  const host = el("next-list");
  if (!trips.length) {
    host.innerHTML = `<p class="trip-meta">Nothing lined up.</p>`;
    return;
  }

  host.innerHTML = trips
    .map((t) => {
      const drive = t.driveMinutes
        ? ` · ${fmtDrive(t.driveMinutes)} drive`
        : "";
      // A goal still on zero is the one worth calling out.
      const unlocks = t.unlocks
        .map(
          (u) =>
            `<span class="next-unlock${u.goal === "club-west-branch" ? " new" : ""}">${escapeHtml(
              shortGoalName(u.name),
            )} +${fmtMiles(u.miles)}</span>`,
        )
        .join("");
      return `<button class="next" type="button" data-trip-id="${escapeHtml(t.id)}" aria-current="false">
        <span class="next-name">${escapeHtml(t.name)}</span>
        <span class="next-meta">${fmtMiles(t.miles)} mi · RM ${t.putIn.riverMile.toFixed(0)}–${t.takeOut.riverMile.toFixed(0)}${drive}</span>
        <span class="next-unlocks">${unlocks}</span>
      </button>`;
    })
    .join("");

  for (const btn of host.querySelectorAll<HTMLButtonElement>(".next")) {
    btn.addEventListener("click", () => {
      const trip = trips.find((t) => t.id === btn.dataset.tripId);
      if (trip) onSelect(trip);
    });
  }
}

function renderDetail(trip: Trip, wps: Waypoint[], onFocus: (name: string) => void) {
  const crew = trip.paddlerCount
    ? `${trip.paddlerCount} paddler${trip.paddlerCount === 1 ? "" : "s"}` +
      (trip.paddlers.length ? ` — ${trip.paddlers.join(", ")}` : "")
    : trip.paddlers.join(", ");

  // A proposal has no date yet, so it gets its logistics line instead of one.
  const when =
    trip.status === "planned"
      ? [
          "Not run yet",
          trip.driveMinutes
            ? `${fmtDrive(trip.driveMinutes)} from Lancaster`
            : "",
        ]
          .filter(Boolean)
          .join(" · ")
      : `${fmtDateRange(trip.date, trip.endDate)}${trip.days.length ? ` · ${trip.days.length} days` : ""}`;

  const unlocks = trip.unlocks?.length
    ? `<p class="detail-notes dim">Would add ${trip.unlocks
        .map((u) => `${fmtMiles(u.miles)} mi to ${escapeHtml(u.name)}`)
        .join(", ")}.</p>`
    : "";

  el("detail-body").innerHTML = `
    <h3>${escapeHtml(trip.name)}</h3>
    <p class="detail-dates">${escapeHtml(when)}</p>
    <dl class="detail-stats">
      <div class="detail-stat"><dt>Distance</dt><dd>${fmtMiles(trip.miles)} mi</dd></div>
      <div class="detail-stat"><dt>Put in</dt><dd>RM ${trip.putIn.riverMile.toFixed(1)}</dd></div>
      <div class="detail-stat"><dt>Took out</dt><dd>RM ${trip.takeOut.riverMile.toFixed(1)}</dd></div>
    </dl>
    ${trip.notes ? `<p class="detail-notes">${escapeHtml(trip.notes)}</p>` : ""}
    ${unlocks}
    ${crew ? `<p class="detail-notes dim">${escapeHtml(crew)}</p>` : ""}
    <div class="ruler-head"><span>River mile</span><span>Downstream &darr;</span></div>
    <div class="ruler" id="ruler"><div class="ruler-spine"></div><svg class="leaders" id="leaders" aria-hidden="true"></svg></div>
  `;
  el("detail").classList.remove("hidden");
  renderRuler(trip, wps, onFocus);
}

/**
 * The mile ruler: a linear river-mile scale down the side of the trip. Dots sit
 * at their true mile, but labels would collide wherever stops bunch up, so they
 * are pushed apart and joined back to the spine by a leader line.
 */
function renderRuler(trip: Trip, wps: Waypoint[], onFocus: (name: string) => void) {
  const ruler = el("ruler");
  const leaders = el<SVGSVGElement & HTMLElement>("leaders");

  // Off-river stops (stores, road landmarks) have no place on a river scale.
  const stops = wps
    .filter((w) => !w.offRiver)
    .sort((a, b) => b.riverMile - a.riverMile);
  if (stops.length < 2) return;

  const top = Math.max(...stops.map((s) => s.riverMile));
  const bottom = Math.min(...stops.map((s) => s.riverMile));
  const span = top - bottom || 1;

  const GAP = 21;
  const PAD = 10;
  const height = Math.max(240, stops.length * GAP + 2 * PAD);
  ruler.style.height = `${height}px`;

  const y = (mile: number) => ((top - mile) / span) * (height - 2 * PAD) + PAD;

  // Camps split the trip into days, so band the spine between them.
  const camps = stops.filter((s) => s.type === "campsite" || s.type === "camp");
  const edges = [top, ...camps.map((c) => c.riverMile), bottom];
  for (let i = 0; i < edges.length - 1; i++) {
    const band = document.createElement("div");
    band.className = "band";
    band.style.top = `${y(edges[i])}px`;
    band.style.height = `${Math.max(1, y(edges[i + 1]) - y(edges[i]))}px`;
    band.style.background = DAY_COLORS[i % DAY_COLORS.length];
    ruler.appendChild(band);
  }

  // Whole-mile ticks at a spacing that keeps the axis readable at any length.
  const step = span > 120 ? 25 : span > 60 ? 10 : span > 25 ? 5 : span > 10 ? 2 : 1;
  for (let m = Math.ceil(bottom / step) * step; m <= top; m += step) {
    const tick = document.createElement("div");
    tick.className = "tick";
    tick.style.top = `${y(m)}px`;
    tick.textContent = String(m);
    ruler.appendChild(tick);
  }

  // Lay labels out top-down, never closer together than GAP; if that runs off
  // the bottom, push the overflow back up.
  const nodes = stops.map((s) => ({ s, trueY: y(s.riverMile), y: 0 }));
  let cursor = -Infinity;
  for (const n of nodes) {
    n.y = Math.max(n.trueY, cursor + GAP);
    cursor = n.y;
  }
  if (cursor > height - PAD) {
    let ceiling = height - PAD + GAP;
    for (let i = nodes.length - 1; i >= 0; i--) {
      nodes[i].y = Math.min(nodes[i].y, ceiling - GAP);
      ceiling = nodes[i].y;
    }
  }

  const NS = "http://www.w3.org/2000/svg";
  for (const { s, trueY, y: labelY } of nodes) {
    const major = MAJOR.has(s.type);
    const color = WAYPOINT_COLORS[s.type] ?? WAYPOINT_FALLBACK;

    const notch = document.createElement("div");
    notch.className = "notch";
    notch.style.top = `${trueY}px`;
    notch.style.background = major ? color : "#2a5563";
    ruler.appendChild(notch);

    const path = document.createElementNS(NS, "path");
    path.setAttribute("d", `M 9 ${trueY} C 13 ${trueY}, 13 ${labelY}, 17 ${labelY}`);
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", Math.abs(labelY - trueY) > 1 ? "#2a5563" : "transparent");
    path.setAttribute("stroke-width", "1.5");
    path.setAttribute("opacity", "0.7");
    leaders.appendChild(path);

    const button = document.createElement("button");
    button.type = "button";
    button.className = `node${major ? " is-major" : ""}`;
    button.style.top = `${labelY}px`;
    button.title = `${s.name} · mile ${s.riverMile}${s.approximate ? " (approx)" : ""}`;
    button.innerHTML =
      `<span class="dot" style="background:${color}"></span>` +
      `<span class="lbl">${escapeHtml(s.name)}` +
      (s.approximate ? ` <span class="approx">approx</span>` : "") +
      `</span>`;
    button.addEventListener("click", () => onFocus(s.name));
    ruler.appendChild(button);
  }
}

main().catch((err) => {
  console.error(err);
  el("trip-list").innerHTML =
    `<p class="trip-meta">Could not load trip data. Run <code>npm run build</code>.</p>`;
});
